import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Alert,
    Breadcrumb,
    Button,
    Col,
    Descriptions,
    Empty,
    Flex,
    Form,
    Image,
    Input,
    Modal,
    Row,
    Spin,
    Tag,
    Typography,
} from "antd";
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    FileImageOutlined,
    PhoneOutlined,
    SafetyCertificateOutlined,
    SwapRightOutlined,
    UnlockOutlined,
    UserOutlined,
} from "@ant-design/icons";

import useViewRecoverRequest from "./useViewRecoverRequest";
import PageHeader from "../../../components/PageHeader";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { formatDateStringToDateAndTime } from "../../../utils/Conversores";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

export default function ViewRecoverRequest() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const {
        isLoading,
        isLoadingImages,
        isSubmiting,
        recoverRequest,
        images,
        observation,
        observationError,
        isPending,
        isApproved,
        isRefused,
        handleObservationChange,
        handleApproveRequest,
        handleRefuseRequest,
        isConfirmApproveRequestModalVisible,
        isConfirmRefuseRequestModalVisible,
        handleConfirmApproveRequest,
        handleConfirmRefuseRequest,
        handleCancel,
        formattedData,
    } = useViewRecoverRequest();

    if (isLoading && !recoverRequest) {
        return (
            <div
                style={{
                    minHeight: 350,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Spin size="large" tip="Carregando solicitação..." />
            </div>
        );
    }

    const statusTag = isApproved ? (
        <Tag color="success" icon={<CheckCircleOutlined />}>
            Aprovado
        </Tag>
    ) : isRefused ? (
        <Tag color="error" icon={<CloseCircleOutlined />}>
            Recusado
        </Tag>
    ) : (
        <Tag color="warning" icon={<ClockCircleOutlined />}>
            Em Análise
        </Tag>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Breadcrumb Header */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/recuperacao-acesso">Recuperação de Acesso</Link>,
                        },
                        {
                            title: `Solicitação #${recoverRequest?.id ?? "--"}`,
                        },
                    ]}
                />
            </div>

            {/* PageHeader */}
            <PageHeader
                icon={<UnlockOutlined />}
                iconBg="linear-gradient(135deg, #fa8c16 0%, #fa541c 100%)"
                title={`Solicitação de Recuperação #${recoverRequest?.id ?? "--"}`}
                tag={statusTag}
                subtitle={`Solicitante: ${formattedData.userName} | CPF: ${formattedData.userCpf}`}
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/recuperacao-acesso")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Solicitações
                    </Button>
                }
            />

            {/* Contextual Status Alert Banner */}
            {isApproved && (
                <Alert
                    type="success"
                    showIcon
                    message={`Solicitação Aprovada ${formattedData.dateResponse ? `em ${formatDateStringToDateAndTime(formattedData.dateResponse)}` : ""}`}
                    description={
                        <div>
                            <div>
                                <strong>Operador responsável:</strong> {formattedData.operatorName || "Operador do Painel"}
                            </div>
                            {recoverRequest?.observation && (
                                <div style={{ marginTop: 4 }}>
                                    <strong>Observação:</strong> {recoverRequest.observation}
                                </div>
                            )}
                        </div>
                    }
                    style={{ borderRadius: 8 }}
                />
            )}

            {isRefused && (
                <Alert
                    type="error"
                    showIcon
                    message={`Solicitação Recusada ${formattedData.dateResponse ? `em ${formatDateStringToDateAndTime(formattedData.dateResponse)}` : ""}`}
                    description={
                        <div>
                            <div>
                                <strong>Operador responsável:</strong> {formattedData.operatorName || "Operador do Painel"}
                            </div>
                            <div style={{ marginTop: 4 }}>
                                <strong>Motivo da recusa:</strong> {recoverRequest?.observation || "Não especificado"}
                            </div>
                        </div>
                    }
                    style={{ borderRadius: 8 }}
                />
            )}

            {isPending && (
                <Alert
                    type="info"
                    showIcon
                    message="Solicitação Pendente de Análise"
                    description="Confira as informações cadastrais do cidadão e compare com as fotos dos documentos comprobatórios e a selfie antes de aprovar ou recusar a alteração do número de telefone."
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* Main Content Grid */}
            <Row gutter={[16, 16]}>
                {/* CARD 1: Dados do Cidadão e Telefones */}
                <Col xs={24} lg={12}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            padding: "18px 20px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                            }}
                        >
                            <UserOutlined style={{ color: "#fa8c16", fontSize: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Dados do Solicitante
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Informações cadastrais e detalhes da solicitação
                                </Text>
                            </div>
                        </div>

                        <Descriptions
                            column={1}
                            size="small"
                            bordered
                            style={{
                                marginBottom: 16,
                                background: isDark ? "#141414" : "#f8fafc",
                            }}
                        >
                            <Descriptions.Item label="Nome Completo">
                                <span style={{ fontWeight: 600 }}>{formattedData.userName}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="CPF">
                                <span>{formattedData.userCpf}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Data da Solicitação">
                                <span>
                                    {formattedData.createdAt
                                        ? formatDateStringToDateAndTime(formattedData.createdAt)
                                        : "--"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status da Solicitação">
                                {statusTag}
                            </Descriptions.Item>
                        </Descriptions>

                        {/* Painel Destacado de Troca de Telefone */}
                        <div
                            style={{
                                background: isDark ? "#141414" : "#fff7e6",
                                border: `1px solid ${isDark ? "#262626" : "#ffd591"}`,
                                borderRadius: 8,
                                padding: "14px 16px",
                                marginTop: "auto",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                                <PhoneOutlined style={{ color: "#fa8c16", fontSize: 15 }} />
                                <Text strong style={{ fontSize: 13, color: isDark ? "#ffd591" : "#d46b08" }}>
                                    Alteração do Número de Celular Solicitada
                                </Text>
                            </div>

                            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
                                <div style={{ flex: 1, minWidth: 130 }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                                        Telefone Atual Cadastrado
                                    </Text>
                                    <Tag
                                        style={{
                                            fontSize: 13,
                                            padding: "4px 10px",
                                            background: isDark ? "#222" : "#f1f5f9",
                                            borderColor: isDark ? "#333" : "#cbd5e1",
                                            color: isDark ? "#aaa" : "#64748b",
                                            fontFamily: "monospace",
                                            borderRadius: 4,
                                        }}
                                    >
                                        {formattedData.existingPhone}
                                    </Tag>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <SwapRightOutlined style={{ fontSize: 22, color: "#fa8c16" }} />
                                </div>

                                <div style={{ flex: 1, minWidth: 130 }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                                        Novo Telefone Solicitado
                                    </Text>
                                    <Tag
                                        color="blue"
                                        style={{
                                            fontSize: 13,
                                            padding: "4px 10px",
                                            fontWeight: 600,
                                            fontFamily: "monospace",
                                            borderRadius: 4,
                                        }}
                                    >
                                        {formattedData.newPhone}
                                    </Tag>
                                </div>
                            </Flex>
                        </div>
                    </div>
                </Col>

                {/* CARD 2: Documentos Comprobatórios (Fotos) */}
                <Col xs={24} lg={12}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            padding: "18px 20px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <FileImageOutlined style={{ color: "#1677ff", fontSize: 16 }} />
                                <div>
                                    <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                        Documentos Anexados ({images.length})
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Fotos de identificação e documentos enviados pelo cidadão
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {isLoadingImages ? (
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 180,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                }}
                            >
                                <Spin />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Carregando imagens do servidor seguro...
                                </Text>
                            </div>
                        ) : images.length > 0 ? (
                            <div>
                                <Paragraph
                                    type="secondary"
                                    style={{ fontSize: 12, marginBottom: 14 }}
                                >
                                    <EyeOutlined style={{ marginRight: 4 }} />
                                    Clique nas imagens abaixo para ampliar em tela cheia com recursos de zoom e rotação.
                                </Paragraph>

                                <Image.PreviewGroup>
                                    <Row gutter={[14, 14]}>
                                        {images.map((url, index) => (
                                            <Col xs={24} sm={12} key={index}>
                                                <div
                                                    style={{
                                                        background: isDark ? "#141414" : "#f8fafc",
                                                        border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                                                        borderRadius: 8,
                                                        padding: 8,
                                                        textAlign: "center",
                                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                                    }}
                                                >
                                                    <Image
                                                        src={url}
                                                        alt={`Documento ${index + 1}`}
                                                        style={{
                                                            width: "100%",
                                                            height: 160,
                                                            objectFit: "cover",
                                                            borderRadius: 6,
                                                        }}
                                                    />
                                                    <div style={{ marginTop: 6 }}>
                                                        <Tag style={{ margin: 0, fontSize: 11 }}>
                                                            Anexo #{index + 1}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Image.PreviewGroup>
                            </div>
                        ) : (
                            <div
                                style={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "30px 0",
                                }}
                            >
                                <Empty
                                    description="Nenhum documento ou foto anexado a esta solicitação."
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            </div>
                        )}
                    </div>
                </Col>

                {/* CARD 3: Parecer e Decisão do Operador */}
                {isPending && (
                    <Col xs={24}>
                        <div
                            style={{
                                background: isDark ? "#1a1a1a" : "#ffffff",
                                border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                                boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                                borderRadius: 10,
                                padding: "20px 22px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 16,
                                    paddingBottom: 10,
                                    borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                                }}
                            >
                                <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                                <div>
                                    <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                        Parecer da Análise e Decisão
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Insira sua observação ou justificativa (obrigatória em caso de recusa)
                                    </Text>
                                </div>
                            </div>

                            <Form layout="vertical">
                                <Form.Item
                                    label="Observação / Justificativa"
                                    validateStatus={observationError ? "error" : undefined}
                                    help={observationError}
                                    style={{ marginBottom: 20 }}
                                >
                                    <TextArea
                                        rows={3}
                                        value={observation}
                                        onChange={(e) => handleObservationChange(e.target.value)}
                                        placeholder="Ex: Documento legível e selfie conferem com os dados cadastrais. / Foto ilegível ou documento inválido..."
                                        style={{ resize: "vertical" }}
                                    />
                                </Form.Item>

                                <Flex justify="flex-end" align="center" gap={12} wrap="wrap">
                                    <Button
                                        danger
                                        icon={<CloseCircleOutlined />}
                                        size="large"
                                        disabled={isSubmiting}
                                        loading={isSubmiting}
                                        onClick={handleRefuseRequest}
                                        style={{
                                            fontWeight: 500,
                                            borderRadius: 8,
                                            minWidth: 160,
                                        }}
                                    >
                                        Recusar Solicitação
                                    </Button>

                                    <Button
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        size="large"
                                        disabled={isSubmiting}
                                        loading={isSubmiting}
                                        onClick={handleApproveRequest}
                                        style={{
                                            background: "#52c41a",
                                            borderColor: "#52c41a",
                                            fontWeight: 600,
                                            borderRadius: 8,
                                            minWidth: 160,
                                        }}
                                    >
                                        Aprovar Solicitação
                                    </Button>
                                </Flex>
                            </Form>
                        </div>
                    </Col>
                )}
            </Row>

            {/* Modal de Confirmação de Aprovação */}
            <Modal
                open={isConfirmApproveRequestModalVisible}
                onCancel={handleCancel}
                onOk={handleConfirmApproveRequest}
                confirmLoading={isSubmiting}
                okText="Sim, Aprovar"
                cancelText="Cancelar"
                okButtonProps={{
                    style: { background: "#52c41a", borderColor: "#52c41a" },
                    loading: isSubmiting,
                }}
                cancelButtonProps={{
                    disabled: isSubmiting,
                }}
                centered
                title={
                    <Flex align="center" gap={8}>
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />
                        <span>Confirmar Aprovação da Recuperação</span>
                    </Flex>
                }
            >
                <Paragraph style={{ marginTop: 14 }}>
                    Deseja realmente aprovar a solicitação de recuperação de acesso do cidadão{" "}
                    <strong>{formattedData.userName}</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : "#f6ffed",
                        border: `1px solid ${isDark ? "#262626" : "#b7eb8f"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 13,
                    }}
                >
                    <div>
                        O telefone cadastrado será atualizado para:{" "}
                        <strong style={{ color: "#52c41a" }}>{formattedData.newPhone}</strong>
                    </div>
                </div>
            </Modal>

            {/* Modal de Confirmação de Recusa */}
            <Modal
                open={isConfirmRefuseRequestModalVisible}
                onCancel={handleCancel}
                onOk={handleConfirmRefuseRequest}
                confirmLoading={isSubmiting}
                okText="Sim, Recusar"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{
                    loading: isSubmiting,
                }}
                cancelButtonProps={{
                    disabled: isSubmiting,
                }}
                centered
                title={
                    <Flex align="center" gap={8}>
                        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />
                        <span>Confirmar Recusa da Recuperação</span>
                    </Flex>
                }
            >
                <Paragraph style={{ marginTop: 14 }}>
                    Deseja realmente recusar a solicitação de recuperação de acesso do cidadão{" "}
                    <strong>{formattedData.userName}</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : "#fff1f0",
                        border: `1px solid ${isDark ? "#262626" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 13,
                    }}
                >
                    <div>
                        <strong>Motivo registrado:</strong> {observation || "Não informado"}
                    </div>
                </div>
            </Modal>
        </div>
    );
}
